import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { API_BASE_URL, authFetch } from '../config/api';
import { useAuth } from '../context/AuthContext';

type SendState = 'idle' | 'sending' | 'done' | 'error';

/**
 * Auth-gated button that pushes an article URL into the existing knowledge
 * ingestion pipeline (POST /api/knowledge/ingest -> worker job -> staging
 * proposal). Renders nothing when logged out.
 */
export default function SendToKnowledgeCenter({ url }: { url: string | null }) {
  const { isAuthenticated } = useAuth();
  const [state, setState] = useState<SendState>('idle');
  const [jobId, setJobId] = useState<number | null>(null);
  const [error, setError] = useState('');

  if (!isAuthenticated || !url) return null;

  async function send(e: React.MouseEvent) {
    e.stopPropagation();
    setState('sending');
    try {
      const res = await authFetch(`${API_BASE_URL}/api/knowledge/ingest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url }),
      });
      if (!res.ok) throw new Error(await res.text());
      const data = (await res.json()) as { ok: boolean; job_id: number };
      setJobId(data.job_id);
      setState('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Request failed');
      setState('error');
    }
  }

  if (state === 'done') {
    return (
      <span className="send-to-kc send-to-kc--done">
        Job #{jobId} started — review in <Link to="/knowledge/staging">staging</Link>
      </span>
    );
  }

  return (
    <span className="send-to-kc">
      <button
        type="button"
        className="send-to-kc__btn"
        onClick={send}
        disabled={state === 'sending'}
      >
        {state === 'sending' ? 'Sending…' : 'Send to Knowledge Center'}
      </button>
      {state === 'error' && <span className="send-to-kc__error" title={error}>Failed — try again</span>}
    </span>
  );
}
